import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight } from 'lucide-react';
import { FadeUp, TextReveal } from '@/components/site/Reveal';
import { fetchPosts, STATIC } from '@/lib/cms';
import Pagination from '@/components/site/Pagination';
import SEO from '@/components/site/SEO';

const PER_PAGE = 6;

export default function Blog() {
  const [items, setItems] = useState(STATIC.posts);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [page, setPage] = useState(1);
  useEffect(() => { fetchPosts().then(setItems); }, []);
  useEffect(() => { setPage(1); }, [q, cat]);

  const cats = useMemo(() => ['All', ...Array.from(new Set(items.map(i => i.category)))], [items]);
  const filtered = items.filter(i => (cat === 'All' || i.category === cat) && (q === '' || i.title.toLowerCase().includes(q.toLowerCase())));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const featured = items.find(i => i.featured) || items[0];

  return (
    <>
      <SEO title="Journal — Slinkflow" description="Insights and articles from the Slinkflow team." />
      <section className="relative overflow-hidden pt-40 pb-16 lg:pt-56 lg:pb-24">
        <div className="absolute -top-32 left-0 h-[520px] w-[520px] hero-gradient-orb" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Journal</p>
          <h1 className="mt-6 max-w-5xl font-display text-[56px] font-light leading-[1.02] tracking-[-0.03em] md:text-[88px] lg:text-[104px]">
            <TextReveal text="Field notes from" />
            <br />
            <span className="gradient-text font-medium"><TextReveal text="the studio." delay={0.2} /></span>
          </h1>
        </div>
      </section>

      {featured && (
        <section className="pb-16">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <Link to={`/blog/${featured.slug}`} className="group grid items-center gap-10 lg:grid-cols-2" data-cursor data-cursor-label="Read">
              <div className="overflow-hidden rounded-3xl bg-ink-50">
                <div className="aspect-[4/3] overflow-hidden"><img src={featured.cover} alt={featured.title} className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" loading="lazy" /></div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-cyan">Featured · {featured.category}</p>
                <h2 className="mt-4 font-display text-[36px] font-light leading-[1.1] md:text-[52px]">{featured.title}</h2>
                <p className="mt-4 text-[17px] leading-relaxed text-ink-500">{featured.excerpt}</p>
                <p className="mt-6 inline-flex items-center gap-2 font-medium text-ink-900">Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></p>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="py-12">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-wrap items-center gap-4">
          <div className="flex w-full max-w-md items-center gap-2 rounded-full border border-ink-100 bg-white px-5 py-3">
            <Search className="h-4 w-4 text-ink-400" />
            <input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts" className="flex-1 bg-transparent text-[15px] focus:outline-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            {cats.map(c => (<button key={c} onClick={() => setCat(c)} className={`rounded-full px-4 py-2 text-sm ${cat === c ? 'bg-ink-900 text-white' : 'border border-ink-100 bg-white text-ink-700 hover:border-cyan hover:text-cyan'}`}>{c}</button>))}
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {paged.map((b, i) => (
              <FadeUp key={b.id} delay={i * 0.04}>
                <Link to={`/blog/${b.slug}`} data-cursor className="group block overflow-hidden rounded-3xl bg-white shadow-soft border border-ink-100 transition hover:-translate-y-1 hover:shadow-hover">
                  <div className="aspect-[16/10] overflow-hidden"><img src={b.cover} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" /></div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-xs text-ink-400">
                      <span className="rounded-full bg-cyan-soft px-2.5 py-1 text-[11px] font-medium uppercase tracking-widest text-cyan-700">{b.category}</span>
                      <span>{b.read_minutes} min read</span>
                    </div>
                    <h3 className="mt-4 font-display text-[22px] text-ink-900 group-hover:text-cyan">{b.title}</h3>
                    <p className="mt-2 text-[14px] text-ink-500">{b.excerpt}</p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
          <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} />
        </div>
      </section>
    </>
  );
}
